import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TreatmentType, TreatmentProviderFilterData, ServiceOption, DemographicOption } from '../../models/TreatmentProviders.models';
import { NgIf, NgFor } from '@angular/common';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatExpansionPanelDescription, MatExpansionPanelActionRow } from '@angular/material/expansion';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatSelect } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { TranslateModule } from '@ngx-translate/core';
import { treatmentConfigurations } from '../../data/keywords';
import { LocationService } from '../../location.service';


@Component({
  selector: 'treatment-locator-search-filters',
  templateUrl: './treatment-locator-search-filters.component.html',
  styleUrls: ['./treatment-locator-search-filters.component.scss'],
  standalone: true,
  imports: [
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    MatRadioGroup,
    FormsModule, MatRadioButton, NgIf, MatFormField, MatSelect, ReactiveFormsModule, NgFor, MatOption, MatSlideToggle, MatLabel, MatInput, MatIconButton, MatSuffix, MatIcon, MatExpansionPanelActionRow, MatButton, TranslateModule]
})
export class TreatmentLocatorSearchFiltersComponent implements OnInit {
  readonly TreatmentType = TreatmentType;

  treatmentTypes: { label: string; value: TreatmentType }[] = [
    { label: 'Crisis Centers', value: TreatmentType.CrisisCenters },
    { label: 'Mental Health', value: TreatmentType.MentalHealth },
    { label: 'Prevention', value: TreatmentType.Prevention },
    { label: 'Substance Use', value: TreatmentType.SubstanceUse },
    { label: 'Medication Disposal Locations', value: TreatmentType.DropOff },
    // { label: 'Support Services', value: TreatmentType._211Resources },
    // { label: 'Mental Health Support Groups', value: TreatmentType.HealthServies },
    // { label: 'Substance Use Support Groups', value: TreatmentType.SupportGroups }
  ];

  ranges = [
    { label: '25 miles', value: 25 },
    { label: '50 miles', value: 50 },
    { label: '100 miles', value: 100 },
    { label: '150 miles', value: 150 }
  ];

  treatmentType: TreatmentType = TreatmentType.SubstanceUse;
  range: number = 25;
  useDeviceLocation: boolean = true;
  disableDeviceLocationToggle: boolean = false;
  searchAddress: string = '';

  servicesControl = new FormControl([]);
  demographicsControl = new FormControl([]);
  payerControl = new FormControl([]);

  serviceList: ServiceOption[] = [];
  demographicList: DemographicOption[] = [];

  payerSources: { label: string, value: string }[] = [
    { label: 'ADMH State Funded Services (Free/Low-cost)', value: 'ADMH State Funded Services' },
    { label: 'Sliding Scale', value: 'Sliding Scale' },
    { label: 'Self-Pay', value: 'Self-Pay' },
    { label: 'Medicare', value: 'Medicare' },
    { label: 'Medicaid', value: 'Medicaid' },
    { label: 'Private Insurance', value: 'Private Insurance' }
  ];

  @Output() treatmentTypeChanged = new EventEmitter<TreatmentType>();
  @Output() applyFilters = new EventEmitter<TreatmentProviderFilterData>();
  @Output() clearFilters = new EventEmitter();

  locationService = inject(LocationService);

  ngOnInit(): void {
    this.treatmentType = TreatmentType.SubstanceUse;
    const config: any = treatmentConfigurations[this.treatmentType];

    if (config) {
      this.serviceList = config.serviceList || [];
      this.demographicList = config.demographicList || [];
    } else {
      this.serviceList = [];
      this.demographicList = [];
    }

  }

  onTreatmentTypeChange(type: TreatmentType) {
    this.onClearFilters();

    this.treatmentType = type;
    const config: any = treatmentConfigurations[this.treatmentType];

    if (config) {
      this.serviceList = config.serviceList || [];
      this.demographicList = config.demographicList || [];
    } else {
      this.serviceList = [];
      this.demographicList = [];
    }
    this.treatmentTypeChanged.emit(type);
  }

  onApplyFilters() {
    if (this.useDeviceLocation) {
      this.locationService.getCurrentPosition()
        .then((position) => {
          this.applyFilters.emit({
            address: this.searchAddress,
            latitude: position?.coords.latitude ?? null,
            longitude: position?.coords.longitude ?? null,
            range: Number(this.range),
            agencyType: Number(this.treatmentType),
            demographics: this.demographicsControl.value,
            servicesOffered: this.servicesControl.value,
            payerSources: this.payerControl.value,
          });
        },
          () => {
            // disable use my device location
            this.useDeviceLocation = false;
            this.disableDeviceLocationToggle = true;
          })
    } else {
      this.applyFilters.emit({
        address: this.searchAddress,
        latitude: null,
        longitude: null,
        range: Number(this.range),
        agencyType: Number(this.treatmentType),
        demographics: this.demographicsControl.value,
        servicesOffered: this.servicesControl.value,
        payerSources: this.payerControl.value,
      });
    }
  }

  onClearFilters() {
    this.servicesControl.reset();
    this.demographicsControl.reset();
    this.searchAddress = '';

    this.clearFilters.emit();
  }

  onToggleLocation() {
    this.useDeviceLocation = !this.useDeviceLocation;
  }

  clearAddress() {
    this.searchAddress = '';
  }

  shouldShowServicesOffered() {
    return this.treatmentType !== TreatmentType.CrisisCenters &&
      this.treatmentType !== TreatmentType.DropOff &&
      this.treatmentType !== TreatmentType.Prevention &&
      this.treatmentType !== TreatmentType.HealthServies &&
      this.treatmentType !== TreatmentType.SupportGroups
  }

  shouldShowDemographics() {
    return this.treatmentType !== TreatmentType.CrisisCenters &&
      this.treatmentType !== TreatmentType.DropOff &&
      this.treatmentType !== TreatmentType.Prevention &&
      this.treatmentType !== TreatmentType._211Resources &&
      this.treatmentType !== TreatmentType.HealthServies &&
      this.treatmentType !== TreatmentType.SupportGroups

  }

  shouldShowPayerSources() {
    return this.treatmentType !== TreatmentType._211Resources &&
      this.treatmentType !== TreatmentType.HealthServies &&
      this.treatmentType !== TreatmentType.SupportGroups
  }

  shouldShowLocationFilter() {
    return this.treatmentType !== TreatmentType.HealthServies &&
      this.treatmentType !== TreatmentType.SupportGroups
  }
}

